/**
 * The indicator registry (plugin shape, SPEC §12.10): id → definition. Built-ins register from
 * `builtins/index.ts`; the owner's list (docs/INDICATORS.md) is added one file at a time.
 * Python-sourced series (strategy host, SPEC §11A.12 / §12.10 route B) are a documented TODO:
 * they will register a definition whose `setup` reads a server-streamed series instead of
 * computing locally (ADR-0018).
 */
import type { AnyIndicatorDef } from "./define.ts";
import { parseTimeframe } from "../bars/timeframe.ts";
import { BAR_SOURCES, type InputSpec } from "./types.ts";

export class IndicatorRegistry {
  private readonly defs = new Map<string, AnyIndicatorDef>();

  register(def: AnyIndicatorDef): void {
    if (this.defs.has(def.id)) throw new Error(`indicator "${def.id}" is already registered`);
    this.defs.set(def.id, def);
  }

  /** Register or replace (user scripts are re-registered when edited). */
  set(def: AnyIndicatorDef): void {
    this.defs.set(def.id, def);
  }

  /** Remove a definition (a deleted user script). */
  unregister(id: string): void {
    this.defs.delete(id);
  }

  get(id: string): AnyIndicatorDef | undefined {
    return this.defs.get(id);
  }

  list(): AnyIndicatorDef[] {
    return [...this.defs.values()];
  }

  /**
   * Dialog search: every whitespace-separated term must match the name, short name, id,
   * category or a keyword (case-insensitive).
   */
  search(query: string): AnyIndicatorDef[] {
    const terms = query
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    return this.list().filter((d) => {
      const hay = [d.name, d.shortName, d.id, d.category, ...(d.keywords ?? [])].join(" ").toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }
}

const HEX_RE = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;

/** One input value, sanitised against its spec (clamped, rounded, or the default). */
export function sanitizeInput(spec: InputSpec, raw: unknown): unknown {
  switch (spec.type) {
    case "int":
    case "float": {
      let v = typeof raw === "number" && Number.isFinite(raw) ? raw : spec.default;
      if (spec.type === "int") v = Math.round(v);
      if (spec.min !== undefined) v = Math.max(spec.min, v);
      if (spec.max !== undefined) v = Math.min(spec.max, v);
      return v;
    }
    case "bool":
      return typeof raw === "boolean" ? raw : spec.default;
    case "enum":
      return typeof raw === "string" && spec.options.includes(raw) ? raw : spec.default;
    case "color":
      return typeof raw === "string" && HEX_RE.test(raw) ? raw : spec.default;
    case "source":
      return typeof raw === "string" &&
        ((BAR_SOURCES as readonly string[]).includes(raw) || /^ind:.+:.+$/.test(raw))
        ? raw
        : spec.default;
    case "timeframe": {
      if (raw === "") return "";
      if (typeof raw !== "string") return spec.default;
      const s = parseTimeframe(raw);
      return s && (s.kind === "time" || s.kind === "session") ? raw : spec.default;
    }
    case "session":
      return raw === "eth" || raw === "rth" ? raw : spec.default;
    case "text":
      return typeof raw === "string" && raw.length <= (spec.maxLength ?? 4096) ? raw : spec.default;
  }
}

/** Every input of `def` from `raw` (missing/invalid → defaults). */
export function resolveParams(
  def: AnyIndicatorDef,
  raw: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, spec] of Object.entries(def.inputs)) out[k] = sanitizeInput(spec, raw[k]);
  return out;
}

/** The defaults of every input. */
export function defaultParams(def: AnyIndicatorDef): Record<string, unknown> {
  return resolveParams(def, {});
}

/** Legend arguments ("14 close"): number/enum/source/timeframe inputs, in declaration order. */
export function legendArgs(def: AnyIndicatorDef, params: Readonly<Record<string, unknown>>): string {
  const out: string[] = [];
  for (const [k, spec] of Object.entries(def.inputs)) {
    const v = params[k];
    if (spec.type === "color" || spec.type === "bool" || spec.legend === false) continue;
    if (spec.type === "source" && typeof v === "string" && v.startsWith("ind:")) {
      out.push(v.slice(4).replace(":", "·"));
      continue;
    }
    if (spec.type === "timeframe" && v === "") continue;
    if (typeof v === "number" || typeof v === "string") out.push(String(v));
  }
  return out.join(" ");
}
